import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Radio } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addFoodCleaning,
  FoodCleaning,
  FoodControlPointInstance,
  FoodManagementControls,
  InstanceStatus,
  updateFoodCleaning,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface CleaningFloorAndHoodModalProps extends ModalProps {
  foodControlPointInstance?: FoodControlPointInstance;
  type:
    | FoodManagementControls.cleaningFloor
    | FoodManagementControls.cleaningHood;
  initialData?: FoodCleaning;
}

export function CleaningFloorAndHoodModal({
  foodControlPointInstance,
  type,
  initialData,
  ...props
}: CleaningFloorAndHoodModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { user } = useAuthStore();

  const notes = {
    note1: t("food.Cleaning is performed"),
    note2: t("food.Other"),
  };

  const [optionsValues, setOptionsValues] = useState<{
    yes: boolean;
    partial: boolean;
    no: boolean;
  }>({
    yes: false,
    partial: false,
    no: false,
  });

  const [correctiveActions, setcorrectiveActions] = useState<string[]>([]);
  const [isCorrectiveActionsNeeded, setIsCorrectiveActionsNeeded] =
    useState(false);

  const onCheck = (value: string) => {
    if (correctiveActions.includes(value)) {
      setcorrectiveActions(correctiveActions.filter((r) => r !== value));
    } else {
      setcorrectiveActions([...correctiveActions, value]);
    }
    if (value === notes.note2) {
      setIsCorrectiveActionsNeeded(!isCorrectiveActionsNeeded);
    }
  };

  const cleaningSchmea = useMemo(
    () =>
      z
        .object({
          remark: z.string().optional(),
        })
        .refine(
          (data) => {
            if (!optionsValues.yes) {
              const isMandatoryFieldSelected = correctiveActions?.includes(
                notes.note2,
              );
              const correctiveActionsFilled =
                data.remark && data.remark?.length > 0;
              if (isMandatoryFieldSelected && correctiveActionsFilled) {
                return true;
              }
              if (!isMandatoryFieldSelected && correctiveActions?.length > 0) {
                return true;
              }
              return false;
            }
            return true;
          },
          {
            message: t("food.Corrective Action Remark Required"),
            path: ["remark"],
          },
        ),
    [notes.note2, optionsValues.yes, correctiveActions, t],
  );

  type CleaningSchema = z.infer<typeof cleaningSchmea>;

  const {
    handleSubmit,
    register,
    reset,
    formState: state,
  } = useForm<CleaningSchema>({
    resolver: zodResolver(cleaningSchmea),
  });
  const mutation = useMutation({
    mutationFn: addFoodCleaning,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Cleaning control has been added"), {
        duration: 4000,
      });
    },
    onError: () => {
      toast.error(t("food.An error occurred while adding cleaning control"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateFoodCleaning,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Cleaning control has been added"), {
        duration: 4000,
      });
    },
    onError: () => {
      toast.error(t("food.An error occurred while adding cleaning control"));
    },
  });

  useEffect(() => {
    if (initialData) {
      setOptionsValues({
        yes: initialData.temperature === 1,
        partial: initialData.temperature === 2,
        no: initialData.temperature === 3,
      });

      setcorrectiveActions(
        initialData?.note
          ? JSON.parse(JSON.parse(initialData?.note).reason)
          : [],
      );

      reset({
        remark: initialData?.note ? JSON.parse(initialData.note).remark : "",
      });
    }
  }, [initialData, reset]);

  const onSubmit = (data: CleaningSchema) => {
    if (!foodControlPointInstance || !user) return;

    const isCompeletedWithErrors = !!correctiveActions.length;

    const payload = {
      companyId: foodControlPointInstance?.companyId,
      temperature: optionsValues.yes ? 1 : optionsValues.partial ? 2 : 3,
      reason: JSON.stringify(correctiveActions),
      remark: data.remark,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      registeredBy: user?.id,
      companyLocationId: foodControlPointInstance?.companyLocationId,
      controlPointInstanceId: foodControlPointInstance?.id,
      foodControlPointInstance,
    };

    if (initialData) {
      updateMutation.mutate(payload);
      return;
    }
    mutation.mutate(payload);
  };

  return (
    <Modal
      title={
        type === FoodManagementControls.cleaningFloor
          ? t("food.Cleaning Floor")
          : t("food.Cleaning Hood")
      }
      {...props}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              props.onClose?.();
            }}
          >
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
    >
      <form>
        <div className="border-b-[1px] border-b-gray-300 pb-2.5">
          <Label
            title={t(
              `food.${type === FoodManagementControls.cleaningHood ? "Hood " : ""}Cleaning Has been done`,
            )}
            itemsAlignment="justify-between"
          >
            <Radio
              onClick={() => {
                setOptionsValues({
                  yes: !optionsValues.yes,
                  partial: false,
                  no: false,
                });
                setcorrectiveActions([]);
                setIsCorrectiveActionsNeeded(false);
              }}
              color="acceptable"
              checked={optionsValues.yes}
            />
          </Label>
          <Label
            title={t(
              `food.${type === FoodManagementControls.cleaningHood ? "Hood " : ""}Cleaning has been done but there are missing things`,
            )}
            itemsAlignment="justify-between"
          >
            <Radio
              onClick={() =>
                setOptionsValues({
                  yes: false,
                  partial: !optionsValues.partial,
                  no: false,
                })
              }
              color="warning"
              checked={optionsValues.partial}
            />
          </Label>

          <Label
            title={t(
              `food.${type === FoodManagementControls.cleaningHood ? "Hood " : ""}Cleaning has not been done`,
            )}
            itemsAlignment="justify-between"
          >
            <Radio
              onClick={() =>
                setOptionsValues({
                  yes: false,
                  partial: false,
                  no: !optionsValues.no,
                })
              }
              color="danger"
              checked={optionsValues.no}
            />
          </Label>
        </div>

        {(optionsValues.no || optionsValues.partial) && (
          <div className="border-b-[1px] border-b-gray-300 pb-2.5">
            <Label
              title={t("food.Corrective Action")}
              labelAlignment="start"
              required
            >
              <div className="flex flex-col">
                <Label contentAlignment="right" title={notes.note1}>
                  <Input
                    size="sm"
                    checkbox
                    type="checkBox"
                    defaultChecked={correctiveActions.includes(notes.note1)}
                    value={notes.note1}
                    onChange={(e) => onCheck(e.target.value)}
                  />
                </Label>
                <Label
                  contentAlignment="right"
                  labelAlignment="start"
                  title={notes.note2}
                >
                  <Input
                    size="sm"
                    checkbox
                    type="checkBox"
                    value={notes.note2}
                    defaultChecked={correctiveActions.includes(notes.note2)}
                    onChange={(e) => onCheck(e.target.value)}
                  />
                </Label>
              </div>
            </Label>
          </div>
        )}
        <div>
          <Label title={t("food.Remarks")} required={isCorrectiveActionsNeeded}>
            <Input {...register("remark")} />
          </Label>
          <ErrorMessage message={state.errors.remark?.message} />
        </div>
      </form>
    </Modal>
  );
}
